import React from 'react';
import type { SiteRagReport } from '../../../../types/siteRag';
import { useEffectiveReportTheme } from '../../../../lib/useEffectiveReportTheme';
import { mergeDevelopmentRisks } from '../../../../lib/phasingRisks';
import { GENERIC_SECTION_INSUFFICIENT_MESSAGE } from '../../../../lib/sectionFallbacks/derivedFallbacks';
import { EditorialDataTable, EditorialBadge, EditorialFigureCaption } from '../../report/editorial';
import { makeRenderCitedText, emptyStateNoteProps } from './feasibilityBodyHelpers';
import { isEditorialLayoutsEnabled } from '../../../../lib/runtimeFlags';

/**
 * #34 P2 STAGE B — Risk Assessment (feasibility). Lifted VERBATIM from the
 * SiteRagReportView inline block (body only; the router shell owns the anchor,
 * heading and ProvenanceStrip). The inline block rendered a ProvenanceStrip
 * itself; the router renders the SAME strip, so the DOM is byte-identical.
 *
 * SECTION-PORT (14B, "ranked register") — degrades to a ranked register via
 * EditorialDataTable because the report carries only a risk LIST (no ordinal
 * likelihood × impact). When gen emits ordinal L/I scores, switch to the 14A
 * EditorialRiskMatrix — do NOT assign random ordinals here.
 */
export const RiskAssessmentSection: React.FC<{ report: SiteRagReport; label?: string }> = ({ report }) => {
  const theme = useEffectiveReportTheme();
  const renderCitedText = makeRenderCitedText(report);
  const mergedDevelopmentRisks = mergeDevelopmentRisks(report);

  if (!(mergedDevelopmentRisks.length || report.risks?.length)) {
    return (
      // N1.5C-flat — graceful-omit note flattens to a muted left-rule note on the
      // cream page in editorial mode (shared seam); flag OFF byte-identical.
      <div {...emptyStateNoteProps(isEditorialLayoutsEnabled(), theme, false)} data-testid="riskAssessment-empty">
        {GENERIC_SECTION_INSUFFICIENT_MESSAGE}
      </div>
    );
  }

  type RegisterRow = {
    key: string;
    name: string;
    bandLabel: string;
    tone: 'negative' | 'warning' | 'positive';
    impact: string;
    mitigation: string;
    isEvidenceGap?: boolean;
  };
  const devRows: RegisterRow[] = mergedDevelopmentRisks.map((risk, idx) => ({
    key: `dev-${idx}`,
    name: risk.category,
    bandLabel: risk.riskLevel === 'High' ? 'High' : risk.riskLevel === 'Medium' ? 'Medium' : 'Low',
    tone: risk.riskLevel === 'High' ? 'negative' : risk.riskLevel === 'Medium' ? 'warning' : 'positive',
    impact: risk.reason,
    mitigation: risk.action,
    isEvidenceGap: false,
  }));
  // BTM-4 — dedupe report.risks against the MERGED dev-risk set
  // (incl. derived phasing risks) so a phasing risk doesn't echo
  // a near-duplicate generic risk below it.
  const devRiskKeywords = mergedDevelopmentRisks.flatMap(dr => {
    const words = (dr.category + ' ' + (dr.reason || '')).toLowerCase().split(/\s+/);
    return words.filter(w => w.length > 4);
  });
  const uniqueRisks = (report.risks ?? []).filter(r => {
    const label = r.label.toLowerCase();
    const impact = (r.impact || '').toLowerCase();
    const matchCount = devRiskKeywords.filter(kw => label.includes(kw) || impact.includes(kw)).length;
    return matchCount < 3;
  });
  const genRows: RegisterRow[] = uniqueRisks.map((risk, idx) => {
    const rawRisk = risk as { id?: string; source?: string };
    const isEvidenceGap =
      rawRisk.source === 'scoring'
      || (typeof rawRisk.id === 'string' && rawRisk.id.startsWith('evidence-gap-'))
      || /not specified in the site evidence\s*[—–-]\s*it is excluded from scoring/i.test(risk.impact || '');
    return {
      key: `gen-${idx}`,
      name: risk.label,
      bandLabel: isEvidenceGap
        ? 'Excluded (Evidence Gap)'
        : risk.severity.charAt(0).toUpperCase() + risk.severity.slice(1),
      tone: isEvidenceGap
        ? 'warning'
        : risk.severity === 'critical' || risk.severity === 'high'
          ? 'negative'
          : risk.severity === 'medium'
            ? 'warning'
            : 'positive',
      impact: risk.impact,
      mitigation: risk.mitigation,
      isEvidenceGap,
    };
  });
  const registerRows = [...devRows, ...genRows];
  if (registerRows.length === 0) return null;
  const evidenceGapCount = registerRows.filter((r) => r.isEvidenceGap).length;
  const assessedRiskCount = registerRows.length - evidenceGapCount;
  return (
    // N1 — the risk register is an EXHIBIT (a ranked table). It is ALREADY
    // classified as a span exhibit by the shared `table` span leaf
    // (SPAN_COLUMNS_LEAVES) — so the `<table>` stays full-width in the N2
    // two-column body WITHOUT any new wrapper element or marker (byte-stable
    // structure with the editorial flag OFF; the surface-parity skeleton is
    // unchanged). The figure caption flows as prose beneath it.
    <>
      <EditorialDataTable
        theme={theme}
        // 3 COLUMNS (user request): the Likelihood column was dropped and its
        // band badge folded UNDER the risk name in the Risk cell, freeing the
        // horizontal room for the Impact + Mitigation prose (which previously
        // wrapped to many short lines). The Risk cell stacks name + badge.
        // (Kept in lock-step with the active inline table in SiteRagReportView.)
        columns={[
          { header: 'Risk', width: '22%' },
          { header: 'Impact' },
          { header: 'Mitigation' },
        ]}
        rows={registerRows.map((row) => [
          <div key="risk" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
            <span>{row.name}</span>
            <EditorialBadge theme={theme} variant="outline" tone={row.tone} wrap style={{ maxWidth: '100%' }}>
              {row.bandLabel}
            </EditorialBadge>
          </div>,
          renderCitedText(row.impact),
          row.mitigation ? renderCitedText(row.mitigation) : '–',
        ])}
      />
      {/* Redhead wave 3 (defect 5a) — tables number on their own report-global
          sequence via the figure counter (was a hardcoded 1). */}
      <EditorialFigureCaption theme={theme} figureKey="riskAssessment:register" prefix="Table">
        {evidenceGapCount > 0
          ? `Risk & evidence-gap register – impact / status and mitigation per identified item (${assessedRiskCount} assessed project-specific risks plus ${evidenceGapCount} unscored evidence gaps).`
          : 'Risk register – likelihood band, impact and mitigation per identified risk.'}
      </EditorialFigureCaption>
    </>
  );
};
